import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../ui/tooltip';
import { AlertCircle, Info, ScanSearch } from 'lucide-react';
import { parseNrcInput, EXAMPLE_INPUTS } from '../../../lib/nrcDecoder/parser';
import { getNrcEntry } from '../../../lib/nrcDecoder/constants';
import ResponseBreakdown from './ResponseBreakdown';
import NrcResultCard from './NrcResultCard';
import NrcReferenceTable from './NrcReferenceTable';
import HowNrcWorks from './HowNrcWorks';

const FieldLabel = ({ children, tip }) => (
  <div className="flex items-center gap-1.5">
    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{children}</label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" data-testid="nrc-input-tip" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const NrcDecoder = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [searchParams] = useSearchParams();

  const decode = (raw) => {
    const value = raw !== undefined ? raw : input;
    const parsed = parseNrcInput(value);
    if (!parsed.ok) {
      setError(parsed.error);
      setResult(null);
      return;
    }
    setError(null);
    setResult({
      entry: getNrcEntry(parsed.nrcCode),
      sidCode: parsed.kind === 'response' ? parsed.sidCode : undefined,
    });
  };

  // Pre-fill from ?input= (used by the DTC Decoder's hand-off link for 7F 19 xx responses).
  useEffect(() => {
    const initial = searchParams.get('input');
    if (initial) {
      setInput(initial);
      decode(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    decode();
  };

  const handleExampleClick = (example) => {
    setInput(example);
    decode(example);
  };

  const handleTableRowSelect = (row) => {
    const nextInput = `0x${row.hex.replace(/^0x/i, '')}`;
    setInput(nextInput);
    setError(null);
    setResult({ entry: getNrcEntry(row.decimal), sidCode: undefined });
  };

  return (
    <div className="space-y-6" data-testid="nrc-decoder">
      <TooltipProvider delayDuration={150}>
        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Decode a Negative Response</CardTitle>
            <CardDescription>
              Enter a single NRC byte, or a full 3-byte negative response — the tool detects which one you gave it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-2">
              <FieldLabel tip="Accepts a bare NRC (0x22, 22) or a full negative response (7F 22 31). Spaces, commas, and 0x prefixes are all optional.">
                Enter NRC or UDS Negative Response
              </FieldLabel>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  className="font-mono"
                  placeholder="e.g. 7F 22 31"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  aria-label="Enter NRC or UDS Negative Response"
                  data-testid="nrc-input"
                />
                <Button type="submit" className="btn-premium shrink-0" data-testid="nrc-decode-button">
                  <ScanSearch className="h-4 w-4" /> Decode
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground mr-1">Examples:</span>
              {EXAMPLE_INPUTS.map((ex) => (
                <Badge
                  key={ex}
                  variant="outline"
                  className="font-mono text-[11px] font-normal cursor-pointer hover:bg-accent"
                  onClick={() => handleExampleClick(ex)}
                  data-testid={`nrc-example-${ex.replace(/\s+/g, '')}`}
                >
                  {ex}
                </Badge>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="nrc-input-error">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </TooltipProvider>

      {result && (
        <div className="space-y-6" data-testid="nrc-decode-result">
          {result.sidCode !== undefined && <ResponseBreakdown sidCode={result.sidCode} nrcCode={result.entry.code} />}
          <NrcResultCard entry={result.entry} sidCode={result.sidCode} />
        </div>
      )}

      <NrcReferenceTable onSelectRow={handleTableRowSelect} />

      <HowNrcWorks />
    </div>
  );
};

export default NrcDecoder;
